import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import useVoiceRecorder from './useVoiceRecorder';
import { MAX_VOICE_CAPTURE_BYTES, VOICE_FILE_ACCEPT } from './voiceTranscriptionService';
import LiveVoiceRecordingStatus from './LiveVoiceRecordingStatus';

const VoiceCaptureDialog = ({ open, child, displayName = 'Voice note', onClose, onReady }) => {
  const [audioBlob, setAudioBlob] = useState(null);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const { recording, error: recorderError, completedBlob, audioLevel, elapsedSeconds, quietInput, clearCompletedBlob, start, stop } = useVoiceRecorder({ maxBytes: MAX_VOICE_CAPTURE_BYTES });
  const previewUrl = useMemo(() => audioBlob ? URL.createObjectURL(audioBlob) : '', [audioBlob]);

  useEffect(() => () => previewUrl && URL.revokeObjectURL(previewUrl), [previewUrl]);
  useEffect(() => {
    if (!open) {
      setAudioBlob(null);
      setFileName('');
      setError('');
    }
  }, [open]);

  useEffect(() => {
    if (!completedBlob) return;
    setAudioBlob(completedBlob);
    const extension = completedBlob.type?.toLowerCase().includes('mp4') ? 'm4a' : 'webm';
    setFileName(`voice-note-${Date.now()}.${extension}`);
    clearCompletedBlob();
  }, [clearCompletedBlob, completedBlob]);

  const selectAudio = (blob, name = '') => {
    if (!blob) {
      setAudioBlob(null);
      setFileName('');
      return;
    }
    if (blob.size > MAX_VOICE_CAPTURE_BYTES) {
      setError('Voice recordings must be 20 MB or smaller.');
      return;
    }
    setError('');
    setAudioBlob(blob);
    setFileName(name || `voice-note-${Date.now()}.webm`);
  };

  const handleFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    selectAudio(file, file?.name || '');
  };

  const handleUseAudio = () => {
    setError('');
    onReady?.({
      audioBlob,
      transcript: '',
      displayName,
      originalFileName: fileName,
      mimeType: audioBlob.type || 'audio/webm',
      transcription: { status: 'pending' },
    });
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={recording ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 7 }}>
        Add a voice note
        <IconButton aria-label="Close voice note" onClick={onClose} disabled={recording} sx={{ position: 'absolute', right: 12, top: 12 }}><CloseRoundedIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ color: 'text.secondary', mb: 2 }}>
          This recording belongs only to {child?.name || 'the selected Care Profile'}. Lifelog keeps the audio and organizes its transcript.
        </Typography>
        {recording ? (
          <LiveVoiceRecordingStatus audioLevel={audioLevel} elapsedSeconds={elapsedSeconds} quietInput={quietInput} onStop={stop} />
        ) : !audioBlob ? (
          <Box sx={{ display: 'grid', gap: 1.25 }}>
            <Button variant="contained" startIcon={<MicRoundedIcon />} onClick={start} sx={{ minHeight: 52, textTransform: 'none', borderRadius: '16px' }}>Start recording</Button>
            <Button variant="outlined" startIcon={<UploadFileRoundedIcon />} onClick={() => inputRef.current?.click()} sx={{ minHeight: 48, textTransform: 'none', borderRadius: '16px' }}>Upload audio</Button>
            <input ref={inputRef} type="file" accept={VOICE_FILE_ACCEPT} hidden onChange={handleFile} />
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', textAlign: 'center' }}>M4A, MP3, WAV, or WebM · 20 MB maximum</Typography>
          </Box>
        ) : (
          <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '18px' }}>
            <Typography sx={{ fontWeight: 700, mb: 1, overflowWrap: 'anywhere' }}>{displayName}</Typography>
            <Box component="audio" controls src={previewUrl} sx={{ width: '100%', display: 'block' }} />
            <Button onClick={() => selectAudio(null)} sx={{ mt: 0.75, textTransform: 'none' }}>Choose a different recording</Button>
          </Box>
        )}
        {(error || recorderError) ? <Alert severity="warning" sx={{ mt: 1.5 }}>{error || recorderError}</Alert> : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={recording} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleUseAudio} disabled={!audioBlob || recording} sx={{ textTransform: 'none' }}>
          Save recording
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoiceCaptureDialog;
